import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';

import { db } from '../firebase/firebase';
import { useLeague } from './useLeague';
import { ModeLeagueType, TokenSelectionProps } from '../leagues';
import { selectTokenRequest } from '../services';
import { useTokensContext } from '../context';

type PublicTokenMap = Record<string, { idToken: number }>;

type PrizeItem = {
  position: number;
  isReferrer: boolean;
  key: string;
};

export type UseTokenSelectorReturn = {
  tokens: PublicTokenMap;
  tokensSold: number;
  isSelecting: boolean;
  isLoadingTokens: boolean;
  selectTokenWithFeedback: (payload: {
    idToken: number;
    mode?: ModeLeagueType;
  }) => Promise<{ success: boolean; message: string }>;
  getPrizeDistribution: (prizePoolData: Record<string, number>) => PrizeItem[];
};

export const useGetTokenSelector = (
  mode: ModeLeagueType = 'main',
): UseTokenSelectorReturn => {
  const { league, currentSeason } = useLeague();

  const idLeague = league?.id;
  const seasonId = String(currentSeason?.seasonNumber ?? '');

  const [rawTokens, setRawTokens] = useState<
    Record<string, TokenSelectionProps>
  >({});

  const [isSelecting, setIsSelecting] = useState(false);
  const [isLoadingTokens, setIsLoadingTokens] = useState(true);

  useEffect(() => {
    if (!idLeague || !seasonId) {
      setRawTokens({});
      setIsLoadingTokens(true);
      return;
    }

    setIsLoadingTokens(true);

    const tokensRef = collection(
      db,
      'leagues',
      idLeague,
      'seasons',
      seasonId,
      'tokens',
      mode,
      'list',
    );

    const unsubscribe = onSnapshot(
      tokensRef,
      (snapshot) => {
        const data: Record<string, TokenSelectionProps> = {};

        snapshot.forEach((doc) => {
          data[doc.id] = doc.data() as TokenSelectionProps;
        });

        setRawTokens(data);
        setIsLoadingTokens(false);
      },
      () => {
        setRawTokens({});
        setIsLoadingTokens(false);
      },
    );

    return () => unsubscribe();
  }, [idLeague, seasonId, mode]);

  const tokens: PublicTokenMap = useMemo(() => {
    const sanitized: PublicTokenMap = {};

    for (const key in rawTokens) {
      const token = rawTokens[key];

      if (token?.idToken !== undefined) {
        sanitized[key] = {
          idToken: token.idToken,
        };
      }
    }

    return sanitized;
  }, [rawTokens]);

  const tokensSold = useMemo(() => Object.keys(rawTokens).length, [rawTokens]);

  const selectTokenWithFeedback = useCallback(
    async ({
      idToken,
      mode: payloadMode,
    }: {
      idToken: number;
      mode?: ModeLeagueType;
    }) => {
      if (!idLeague || !seasonId) {
        return { success: false, message: 'League no cargada' };
      }

      setIsSelecting(true);

      try {
        return await selectTokenRequest({
          leagueId: idLeague,
          seasonId,
          tokenId: idToken,
          mode: payloadMode ?? mode,
        });
      } catch {
        return {
          success: false,
          message: 'Error de conexión',
        };
      } finally {
        setIsSelecting(false);
      }
    },
    [idLeague, seasonId, mode],
  );

  const getPrizeDistribution = useCallback(
    (prizePoolData?: Record<string, number>): PrizeItem[] => {
      if (!prizePoolData) return [];

      const winners: number[] = [];
      const referrers: number[] = [];

      Object.keys(prizePoolData).forEach((key) => {
        if (key.startsWith('w')) winners.push(Number(key.replace('w', '')));
        if (key.startsWith('r')) referrers.push(Number(key.replace('r', '')));
      });

      winners.sort((a, b) => a - b);
      referrers.sort((a, b) => a - b);

      const result: PrizeItem[] = [];
      const max = Math.max(winners.length, referrers.length);

      for (let i = 1; i <= max; i++) {
        if (winners.includes(i)) {
          result.push({
            position: i,
            isReferrer: false,
            key: `w${i}`,
          });
        }

        if (referrers.includes(i)) {
          result.push({
            position: i,
            isReferrer: true,
            key: `r${i}`,
          });
        }
      }

      return result;
    },
    [],
  );

  return {
    tokens,
    tokensSold,
    isSelecting,
    isLoadingTokens,
    selectTokenWithFeedback,
    getPrizeDistribution,
  };
};

export default useGetTokenSelector;

export const useTokenSelector = () => {
  const tokens = useTokensContext();
  return { ...tokens };
};
